import { useEffect, useId, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import { CheckIcon } from "../components/Icons";
import InkPad, { savedStrokesOf } from "../components/InkPad";
import { AnswerField, MathKeypad, numericKeysFor } from "../components/MathKeys";
import ui from "../components/ui.module.css";
import useSentBack from "../hooks/useSentBack";
import {
  decimalSeparator,
  entryTextOf,
  isNumericValue,
  parseNumericEntry,
} from "../data/maths/numeric";


const NAMES = {
  integer: "a whole number",
  decimal: "a decimal",
  fraction: "a fraction",
  percentage: "a percentage",
};
const listOf = (words) =>
  words.length < 2 ? words.join("") : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;

// Why an entry cannot be checked yet, in words a learner can act on.
const problemText = (reason, step) =>
  ({
    empty: "Type your answer first.",
    malformed: "We can't read that number. Check it and try again.",
    "zero-denominator": "A fraction can't have 0 at the bottom.",
    negative: "This answer is not a negative number.",
    representation: `Write your answer as ${listOf(step.representations.map((r) => NAMES[r]))}.`,
    unit: "Choose a unit.",
  })[reason];

// Compute, type, hand in. A readable answer is handed in whether or not it is
// the expected value; the gateway judges it and policy decides whether the
// learner moves on (Handoff 007 §3). Optional working uses the same ink
// surface as drawing: its strokes are kept on this tablet while the learner
// works, and a picture of meaningful working becomes protected evidence when
// the answer is handed in. Working never affects correctness.
const NumericEntryStep = ({ step, saved, verdict, onDone, onProgress, saving }) => {
  const separator = decimalSeparator();
  const accepted = isNumericValue(saved?.value) ? saved : null;
  const [text, setText] = useState(() => (accepted ? entryTextOf(accepted.value, separator) : ""));
  const [unit, setUnit] = useState(() =>
    step.unit ? step.unit.value : step.unitOptions?.some((u) => u.value === accepted?.unit) ? accepted.unit : null
  );
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);
  const pad = useRef(null);
  const timer = useRef(null);
  const messageId = useId();

  useEffect(() => () => clearTimeout(timer.current), []);

  useSentBack(verdict, () => setResult("wrong"));

  const edit = (next) => {
    setText(next);
    setProblem(null);
    setResult(null);
  };

  const check = async () => {
    if (result === "sent" || saving) return;
    const parsed = parseNumericEntry(text, step);
    if (parsed.error) {
      setProblem(parsed.error);
      return;
    }
    if (step.unitOptions && !unit) {
      setProblem("unit");
      return;
    }
    const response = { value: parsed.value, ...(unit ? { unit } : {}) };
    setResult("sent");
    // Meaningful working only: no ink, no picture.
    const blob = step.working ? await pad.current?.exportPng() : null;
    onDone(response, blob || null);
  };

  const prefix = step.unit?.position === "prefix" ? step.unit.label : null;
  const suffix = step.unit?.position === "suffix" ? step.unit.label : null;
  const locked = saving || result === "sent";

  const answer = (
    <div className={ui.numericAnswer}>
      <AnswerField
        value={text}
        onChange={edit}
        label="Your answer"
        prefix={prefix}
        suffix={suffix}
        invalid={Boolean(problem)}
        describedBy={messageId}
        disabled={locked}
      />
      {step.unitOptions && (
        <div className={ui.unitChoices} role="radiogroup" aria-label="Unit">
          {step.unitOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={unit === option.value}
              className={`${ui.unitChoice} ${unit === option.value ? ui.unitChosen : ""}`}
              onClick={() => {
                setUnit(option.value);
                setProblem(null);
                setResult(null);
              }}
              disabled={locked}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
      <MathKeypad
        value={text}
        onChange={edit}
        extraKeys={numericKeysFor(step)}
        disabled={locked}
        label="Number keys"
      />
      <p id={messageId} className={ui.mathProblem} role="alert">
        {problem ? problemText(problem, step) : ""}
      </p>
      <button
        type="button"
        className={`${ui.button} ${ui.large} ${ui.primary}`}
        onClick={check}
        disabled={locked}
      >
        <CheckIcon size={24} />
        {saving ? "Saving…" : "Done"}
      </button>
    </div>
  );

  return (
    <>
      <Prompt step={step} />
      {step.working ? (
        <div className={ui.withWorking}>
          {answer}
          <section className={ui.working} aria-label="Working space">
            <h2 className={ui.workingTitle}>Working</h2>
            <InkPad
              ref={pad}
              template={step.working.template}
              saved={savedStrokesOf(saved?.working)}
              disabled={locked}
              label="Working space"
              className={ui.workingPad}
              onChange={(strokes) =>
                onProgress?.({ working: { format: "strokes-v1", strokes } })
              }
            />
          </section>
        </div>
      ) : (
        answer
      )}
    </>
  );
};

export default NumericEntryStep;
